// /calendar - 7-day strip built from the firm agenda feed
// (GET /api/firm/deadlines): explicit deadlines plus hearings, deduped
// server-side. No fixture path: empty and unreachable states say so.

import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Skeleton, SkeletonGroup } from "../../components/chrome/Skeleton";
import { SEP } from "../../components/chrome/SepList";
import { useFirmAgenda } from "../../hooks/useFirmAgenda";
import { AGENDA_DAYS, isDateOnly, itemDayKey, localKey } from "../../api/firmDeadlines";
import type { AgendaItem } from "../../api/firmDeadlines";

const DOW_LABEL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function weekDays(): Date[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push(d);
  }
  return days;
}

function fmtTime(it: AgendaItem): string {
  if (isDateOnly(it.dueDate, it.allDay)) return "";
  return new Date(it.dueDate).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function eventStyle(it: AgendaItem): CSSProperties | undefined {
  if (it.severity === "critical") return undefined; // .crit class handles it
  if (it.type === "hearing") return { borderLeftColor: "var(--ink)" };
  if (it.severity === "urgent") return { borderLeftColor: "var(--warn-600)" };
  return { borderLeftColor: "var(--olive)" };
}

export function CalendarIndex() {
  const { data, isLoading, isError } = useFirmAgenda();
  const days = weekDays();
  const todayKey = localKey(days[0]);
  const lastKey = localKey(days[6]);

  const byDay = new Map<string, AgendaItem[]>();
  for (const d of days) byDay.set(localKey(d), []);
  for (const it of data?.deadlines ?? []) {
    byDay.get(itemDayKey(it.dueDate, it.allDay))?.push(it);
  }
  const weekTotal = Array.from(byDay.values()).reduce((acc, l) => acc + l.length, 0);
  // The feed is sorted ascending, so the first item past the strip is the
  // earliest thing an empty week should point at.
  const nextUp = (data?.deadlines ?? []).find((it) => itemDayKey(it.dueDate, it.allDay) > lastKey);

  if (isLoading) {
    return (
      <main className="cc-main">
        <div className="cc-body">
          <SkeletonGroup label="Loading calendar">
            <Skeleton width="30%" height={22} />
            <Skeleton width="100%" height={140} radius={10} />
          </SkeletonGroup>
        </div>
      </main>
    );
  }

  return (
    <main className="cc-main">
      <div className="cc-body">
        <div className="cc-index-head">
          <span className="title">Calendar</span>
          {data && (
            <span className="count">
              {weekTotal} {weekTotal === 1 ? "event" : "events"}
              {SEP}next 7 days
            </span>
          )}
          <span className="blurb">
            Deadlines and hearings across active matters. Click any item to open the matter.
          </span>
        </div>

        {isError && (
          <div
            className="cc-card"
            style={{ padding: "10px 18px", marginBottom: 12, color: "var(--warn-600)" }}
          >
            Could not reach the server - the calendar is unavailable.
          </div>
        )}

        {data && (
          <div className="cc-calendar-strip" role="grid" aria-label="Next 7 days">
            {days.map((d) => {
              const key = localKey(d);
              const list = byDay.get(key) ?? [];
              const visible = list.slice(0, 4);
              const overflow = list.length - visible.length;
              return (
                <div
                  key={key}
                  className={"cc-calendar-day" + (key === todayKey ? " today" : "")}
                  role="gridcell"
                >
                  <div className="dow">{DOW_LABEL[d.getDay()]}</div>
                  <div className="dom">{d.getDate()}</div>
                  <div className="events">
                    {visible.map((it, i) => {
                      const time = fmtTime(it);
                      const cls = "cc-calendar-event" + (it.severity === "critical" ? " crit" : "");
                      const title = it.clientName + (it.location ? SEP + it.location : "");
                      const itemKey = `${it.caseId}:${it.dueDate}:${i}`;
                      const body = (
                        <>
                          {time && <span className="when">{time}</span>}
                          {it.label}
                        </>
                      );
                      return it.eId ? (
                        <Link
                          key={itemKey}
                          to={`/matter/${it.eId}`}
                          className={cls}
                          title={title}
                          style={eventStyle(it)}
                        >
                          {body}
                        </Link>
                      ) : (
                        <span key={itemKey} className={cls} title={title} style={eventStyle(it)}>
                          {body}
                        </span>
                      );
                    })}
                    {overflow > 0 && <span className="more">+ {overflow} more</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {data && weekTotal === 0 && (
          <div
            className="cc-card"
            style={{ padding: "10px 18px", marginTop: 12, color: "var(--muted)" }}
          >
            Nothing scheduled in the next 7 days.
            {nextUp
              ? ` Next up: ${nextUp.label} for ${nextUp.clientName} on ${itemDayKey(nextUp.dueDate, nextUp.allDay)}.`
              : ` Nothing on the calendar in the next ${AGENDA_DAYS} days.`}
          </div>
        )}

        {data && (
          <div className="cc-detail-footer">
            <span>{weekTotal} this week</span>
            <span className="sep">{SEP.trim()}</span>
            <span>
              {data.deadlines.length} in the next {AGENDA_DAYS} days
            </span>
          </div>
        )}
      </div>
    </main>
  );
}
